import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { ChatCircleDots, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const REQUEST_TYPES = [
    { value: "payment_question", label: "Payment Question" },
    { value: "missing_hours", label: "Missing Hours" },
    { value: "correction_request", label: "Correction Request" },
    { value: "equipment_request", label: "Equipment Request" },
    { value: "absence_notice", label: "Absence Notice" },
    { value: "general_issue", label: "General Issue" },
];

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "in_review", label: "In Review" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "completed", label: "Completed" },
];

function labelFromType(t) {
    return REQUEST_TYPES.find((r) => r.value === t)?.label || t;
}

export default function Requests() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    // Filters (admin only)
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterType, setFilterType] = useState("all");

    // Submit dialog
    const [submitOpen, setSubmitOpen] = useState(false);
    const [form, setForm] = useState({ type: "payment_question", subject: "", description: "" });

    // Manage dialog (admin)
    const [manageOpen, setManageOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [adminStatus, setAdminStatus] = useState("pending");
    const [adminNotes, setAdminNotes] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = {};
                if (isAdmin) {
                    if (filterStatus !== "all") params.status = filterStatus;
                    if (filterType !== "all") params.type = filterType;
                }
                const { data } = await api.get("/requests", { params });
                if (!cancelled) setRequests(data || []);
            } catch {
                if (!cancelled) toast.error("Failed to load requests");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isAdmin, filterStatus, filterType, refreshTick]);

    const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

    const submitRequest = async () => {
        if (!form.subject.trim() || !form.description.trim()) {
            toast.error("Subject and description are required");
            return;
        }
        try {
            await api.post("/requests", form);
            toast.success("Request submitted");
            setSubmitOpen(false);
            setForm({ type: "payment_question", subject: "", description: "" });
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to submit request");
        }
    };

    const openManage = (req) => {
        setSelectedRequest(req);
        setAdminStatus(req.status);
        setAdminNotes(req.admin_notes || "");
        setManageOpen(true);
    };

    const saveStatus = async () => {
        try {
            await api.patch(`/requests/${selectedRequest.id}`, {
                status: adminStatus,
                admin_notes: adminNotes,
            });
            toast.success("Request updated");
            setManageOpen(false);
            reload();
        } catch {
            toast.error("Failed to update");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this request?")) return;
        try {
            await api.delete(`/requests/${id}`);
            toast.success("Deleted");
            reload();
        } catch {
            toast.error("Failed to delete");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        {isAdmin ? "Admin · All Requests" : "My Requests"}
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Requests & Reports
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">
                        {isAdmin
                            ? "Review and manage all technician requests."
                            : "Submit and track your requests to the admin team."}
                    </p>
                </div>
                {!isAdmin && (
                    <Button
                        onClick={() => setSubmitOpen(true)}
                        className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                        data-testid="submit-request-button"
                    >
                        <Plus size={16} className="mr-1.5" /> New request
                    </Button>
                )}
            </div>

            {/* Admin filters */}
            {isAdmin && (
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Status
                            </Label>
                            <Select value={filterStatus} onValueChange={setFilterStatus}>
                                <SelectTrigger className="h-10 mt-1" data-testid="filter-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All statuses</SelectItem>
                                    {STATUS_OPTIONS.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>
                                            {s.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Type
                            </Label>
                            <Select value={filterType} onValueChange={setFilterType}>
                                <SelectTrigger className="h-10 mt-1" data-testid="filter-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All types</SelectItem>
                                    {REQUEST_TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Requests list */}
            {loading ? (
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-10 text-center text-slate-500">Loading...</CardContent>
                </Card>
            ) : requests.length === 0 ? (
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-12 text-center">
                        <ChatCircleDots size={40} className="mx-auto mb-3 text-slate-300" weight="duotone" />
                        <div className="text-slate-500">No requests yet.</div>
                        {!isAdmin && (
                            <Button
                                onClick={() => setSubmitOpen(true)}
                                className="mt-4 bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            >
                                <Plus size={16} className="mr-1.5" /> Submit your first request
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {requests.map((r) => (
                        <Card
                            key={r.id}
                            className="border-[#E2E8F0] an-card-hover"
                            data-testid={`request-card-${r.id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                                                {labelFromType(r.type)}
                                            </span>
                                            <StatusBadge status={r.status} />
                                            {isAdmin && (
                                                <span className="text-xs text-slate-500">
                                                    by {r.technician_name || "—"}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-base font-semibold text-[#1F2A44]">{r.subject}</h3>
                                        <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-line">
                                            {r.description}
                                        </p>
                                        {r.admin_notes && (
                                            <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-md">
                                                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#003366] mb-1">
                                                    Admin response
                                                </div>
                                                <div className="text-sm text-[#1F2A44]">{r.admin_notes}</div>
                                            </div>
                                        )}
                                        <div className="text-xs text-slate-400 mt-3">
                                            Submitted {new Date(r.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isAdmin ? (
                                            <Button
                                                size="sm"
                                                className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                                                onClick={() => openManage(r)}
                                                data-testid={`manage-request-${r.id}`}
                                            >
                                                Manage
                                            </Button>
                                        ) : (
                                            r.status === "pending" && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="text-[#E6002C] hover:text-[#E6002C] hover:bg-red-50"
                                                    onClick={() => handleDelete(r.id)}
                                                    data-testid={`delete-request-${r.id}`}
                                                >
                                                    <Trash size={14} />
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Submit dialog (technician) */}
            <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            Submit a new request
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Type
                            </Label>
                            <Select
                                value={form.type}
                                onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                            >
                                <SelectTrigger className="h-11" data-testid="request-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {REQUEST_TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Subject
                            </Label>
                            <Input
                                value={form.subject}
                                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                                placeholder="Brief one-line summary"
                                className="h-11"
                                data-testid="request-subject-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Description
                            </Label>
                            <Textarea
                                rows={5}
                                value={form.description}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder="Provide details (dates, hours, site, etc.)"
                                data-testid="request-description-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSubmitOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={submitRequest}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="request-submit-confirm"
                        >
                            Submit
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manage dialog (admin) */}
            <Dialog open={manageOpen} onOpenChange={setManageOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            Manage request
                        </DialogTitle>
                        <DialogDescription>
                            Update the status and add notes that the technician will see.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-4 py-2">
                            <div className="bg-slate-50 rounded-md p-3 text-sm">
                                <div className="text-xs text-slate-500 mb-1">
                                    {labelFromType(selectedRequest.type)} · {selectedRequest.technician_name}
                                </div>
                                <div className="font-medium text-[#1F2A44]">{selectedRequest.subject}</div>
                                <div className="text-slate-600 mt-1 whitespace-pre-line">{selectedRequest.description}</div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Status
                                </Label>
                                <Select value={adminStatus} onValueChange={setAdminStatus}>
                                    <SelectTrigger className="h-11" data-testid="admin-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Admin notes (visible to technician)
                                </Label>
                                <Textarea
                                    rows={3}
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    data-testid="admin-notes-input"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setManageOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={saveStatus}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="admin-save-status-button"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
